import { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, User, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`);
        setOrders(response.data);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'status-pending',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled'
    };
    return colors[status] || 'status-pending';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen pt-24" data-testid="profile-page">
      <div className="container-luxury py-12">
        <h1 className="font-serif text-4xl mb-12">Minha Conta</h1>

        <Tabs defaultValue="orders" className="space-y-8">
          <TabsList className="bg-card border border-white/10 p-1">
            <TabsTrigger 
              value="orders" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="orders-tab"
            >
              <Package className="w-4 h-4 mr-2" />
              Meus Pedidos
            </TabsTrigger>
            <TabsTrigger 
              value="profile"
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="profile-tab"
            >
              <User className="w-4 h-4 mr-2" />
              Dados Pessoais
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-6">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, idx) => (
                  <div key={idx} className="h-24 skeleton" />
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="empty-state">
                <Package className="w-16 h-16 text-muted-foreground mb-6" />
                <h2 className="font-serif text-2xl mb-4">Nenhum pedido encontrado</h2>
                <p className="text-muted-foreground">
                  Você ainda não fez nenhum pedido. Explore nossa coleção!
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div 
                    key={order.id} 
                    className="bg-card border border-white/10 overflow-hidden"
                    data-testid={`order-${order.id}`}
                  >
                    {/* Order Header */}
                    <button
                      onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                      className="w-full p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-white/5 transition-colors"
                    >
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="text-left">
                          <p className="text-sm text-muted-foreground">Pedido</p>
                          <p className="text-lg font-bold text-primary">#{order.id}</p>
                        </div>
                        <div className="text-left">
                          <p className="text-sm text-muted-foreground">Data</p>
                          <p>{formatDate(order.created_at)}</p>
                        </div>
                        <div className="text-left">
                          <p className="text-sm text-muted-foreground">Total</p>
                          <p className="font-bold">${order.total.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex gap-2">
                          <span className={`status-badge ${getStatusColor(order.order_status)}`}>
                            {order.order_status}
                          </span>
                          <span className={`status-badge ${getStatusColor(order.payment_status)}`}>
                            {order.payment_status}
                          </span>
                        </div>
                        {expandedOrder === order.id ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </div>
                    </button>

                    {/* Order Details */}
                    {expandedOrder === order.id && (
                      <div className="border-t border-white/10 p-6 space-y-6 animate-fade-in">
                        {/* Items */}
                        <div>
                          <h4 className="text-sm uppercase tracking-wider font-bold mb-4">Itens</h4>
                          <div className="space-y-3">
                            {order.items.map((item, idx) => (
                              <div key={idx} className="flex justify-between items-center">
                                <div>
                                  <p>{item.product_name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {item.size} | {item.color} | x{item.quantity}
                                  </p>
                                </div>
                                <p className="text-primary">${(item.price * item.quantity).toFixed(2)}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Shipping */}
                        <div>
                          <h4 className="text-sm uppercase tracking-wider font-bold mb-4 flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-primary" />
                            Endereço de Entrega
                          </h4>
                          <div className="text-muted-foreground">
                            <p>{order.shipping_address.street}</p>
                            <p>
                              {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}
                            </p>
                            <p>{order.shipping_address.country}</p>
                          </div>
                        </div>

                        {/* Payment Info */}
                        <div>
                          <h4 className="text-sm uppercase tracking-wider font-bold mb-4">Pagamento</h4>
                          <p className="text-muted-foreground capitalize">
                            {order.payment_method === 'cash' ? 'Dinheiro na Entrega' : 'Zelle'}
                          </p>
                        </div>

                        {order.notes && (
                          <div>
                            <h4 className="text-sm uppercase tracking-wider font-bold mb-4">Observações</h4>
                            <p className="text-muted-foreground">{order.notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="bg-card border border-white/10 p-8 max-w-xl">
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Nome</p>
                  <p className="text-lg" data-testid="profile-name">{user?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Email</p>
                  <p className="text-lg" data-testid="profile-email">{user?.email}</p>
                </div>
                {user?.phone && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Telefone</p>
                    <p className="text-lg">{user.phone}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Membro desde</p>
                  <p className="text-lg">
                    {user?.created_at && new Date(user.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Profile;
